import React, { cloneElement, isValidElement, useId, useMemo, useRef } from 'react';
import { resolveFieldTapTarget, resolveFieldWidth } from './fieldTokens';

function mergeDescribedBy(...ids) {
  return ids.filter(Boolean).join(' ') || undefined;
}

function isInteractiveElement(target) {
  return !!target?.closest?.('button, a, input, textarea, select, summary, [role="button"], [role="link"]');
}

export default function FieldShell({
  children,
  label = null,
  helpText = null,
  error = null,
  inputId = null,
  width = 'default',
  tapTarget = 'compact',
  startAdornment = null,
  endAdornment = null,
  style = null,
  contentStyle = null,
}) {
  const reactId = useId();
  const resolvedInputId = inputId || `field-${reactId}`;
  const helpId = helpText ? `${resolvedInputId}-help` : null;
  const errorId = error ? `${resolvedInputId}-error` : null;
  const inputRef = useRef(null);
  const minTapTarget = resolveFieldTapTarget(tapTarget);
  const resolvedWidth = resolveFieldWidth(width);
  const isControlElement = isValidElement(children);

  const control = useMemo(() => {
    if (!isControlElement) return null;
    return cloneElement(children, {
      id: resolvedInputId,
      ref: inputRef,
      'aria-invalid': error ? true : undefined,
      'aria-describedby': mergeDescribedBy(children.props['aria-describedby'], helpId, errorId),
      style: {
        width: '100%',
        ...(children.props.style || {}),
      },
    });
  }, [children, error, errorId, helpId, isControlElement, resolvedInputId]);

  if (!isControlElement) {
    return children ?? null;
  }

  const focusInput = () => {
    inputRef.current?.focus?.();
  };

  return (
    <div
      data-field-shell=""
      style={{
        width: resolvedWidth,
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        ...style,
      }}
    >
      {label && (
        <label
          htmlFor={resolvedInputId}
          style={{ cursor: 'pointer' }}
          onClick={focusInput}
        >
          {label}
        </label>
      )}
      <div
        data-field-shell-content=""
        style={{
          width: '100%',
          minHeight: `${minTapTarget}px`,
          display: 'flex',
          alignItems: 'stretch',
          gap: '8px',
          ...contentStyle,
        }}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget || !isInteractiveElement(event.target)) {
            event.preventDefault();
            focusInput();
          }
        }}
      >
        {startAdornment && (
          <div
            data-field-shell-start=""
            style={{
              minHeight: `${minTapTarget}px`,
              minWidth: `${minTapTarget}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {startAdornment}
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'stretch' }}>
          {control}
        </div>
        {endAdornment && (
          <div
            data-field-shell-end=""
            style={{
              minHeight: `${minTapTarget}px`,
              minWidth: `${minTapTarget}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {endAdornment}
          </div>
        )}
      </div>
      {helpText && (
        <div id={helpId}>
          {helpText}
        </div>
      )}
      {error && (
        <div id={errorId}>
          {error}
        </div>
      )}
    </div>
  );
}
